// React component

import { FEATURES } from "@/constants/features";
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from "@/components/ui/navigation-menu";
import { motion } from "framer-motion";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import useFeatureFlag from "@/hooks/useFeatureFlag";

export default function FeaturesDropdown() {
  const location = useLocation();
  const pathname = location.pathname;
  const isRealtimePage = pathname?.includes("/features/real-time");
  const navigate = useNavigate();

  const isGlobalCdnEnabled = useFeatureFlag("globalCdn");
  const isPerformanceEnabled = useFeatureFlag("performance");
  const isSecurityEnabled = useFeatureFlag("security");
  const isAiSolutionsEnabled = useFeatureFlag("aiSolutions");
  const isDevelopmentEnabled = useFeatureFlag("development");
  const isAnalyticsEnabled = useFeatureFlag("analytics");
  const isAccessControlEnabled = useFeatureFlag("accessControl");
  const isRealtimeEnabled = useFeatureFlag("realtime");

  const filteredFeatures = FEATURES.filter((feature) => {
    switch (feature.title) {
      case "Global CDN":
        return isGlobalCdnEnabled;
      case "Performance":
        return isPerformanceEnabled;
      case "Security":
        return isSecurityEnabled;
      case "AI Solutions":
        return isAiSolutionsEnabled;
      case "Development":
        return isDevelopmentEnabled;
      case "Analytics":
        return isAnalyticsEnabled;
      case "Access Control":
        return isAccessControlEnabled;
      case "Real-time":
        return isRealtimeEnabled;
      default:
        return true;
    }
  });

  return (
    <NavigationMenu data-oid="rkwudyx">
      <NavigationMenuList data-oid="i:94kai">
        <NavigationMenuItem data-oid=".k_zykw">
          <NavigationMenuTrigger
            className={`text-xl pl-4 cursor-pointer ${
              isRealtimePage
                ? "relative z-10 data-[state=open]:bg-pink-500/10"
                : ""
            }`}
            onClick={() => navigate("/features")}
            data-oid="jqpf:1y"
          >
            <span data-oid="ovxhvlv">Features</span>
          </NavigationMenuTrigger>
          <NavigationMenuContent data-oid="ghgw9_8">
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 10 }}
              className={`grid w-[600px] gap-3 p-4 md:grid-cols-2 ${
                isRealtimePage
                  ? "bg-background/90 backdrop-blur-xl border border-pink-500/10"
                  : ""
              }`}
              data-oid="t.:sbfr"
            >
              {isRealtimePage && (
                <div
                  className="absolute inset-0 z-0 overflow-hidden"
                  data-oid="n:h7_j1"
                >
                  <div
                    className="absolute top-1/4 right-1/4 w-[150px] h-[150px] bg-pink-500/5 rounded-full blur-3xl"
                    data-oid="3m7piaj"
                  ></div>
                  <div
                    className="absolute bottom-1/3 left-1/3 w-[100px] h-[100px] bg-blue-500/5 rounded-full blur-3xl"
                    data-oid="l9upn8o"
                  ></div>
                </div>
              )}

              {filteredFeatures.map((feature) => {
                const Icon = feature.icon;
                const isActive = pathname === `/features/${feature.slug}`;
                const isRealTimeFeature = feature.slug === "real-time";

                return (
                  <NavigationMenuLink asChild data-oid="io59g2y">
                    <Link
                      key={feature.slug}
                      to={`/features/${feature.slug}`}
                      className={`block select-none rounded-md p-3 leading-none no-underline outline-none transition-colors 
                        ${
                          isRealtimePage
                            ? `relative z-10 hover:bg-pink-500/10 focus:bg-pink-500/10 
                             ${isActive || isRealTimeFeature ? "bg-pink-500/5" : ""}`
                            : "hover:bg-accent hover:text-accent-foreground focus:bg-accent focus:text-accent-foreground"
                        } group`}
                      data-oid="3:go7dn"
                    >
                      <div
                        className="flex items-start gap-3"
                        data-oid="b5xc2o-"
                      >
                        <div
                          className={`${feature.color} p-2 rounded-lg ${
                            isRealtimePage
                              ? "bg-background/50 group-hover:bg-background/80"
                              : "bg-accent/50 group-hover:bg-accent"
                          }`}
                          data-oid="_v439vt"
                        >
                          <Icon className="h-5 w-5" data-oid="gm1qzcj" />
                        </div>
                        <div data-oid="-kba1pr">
                          <div
                            className={`font-medium mb-1 ${
                              isRealTimeFeature && isRealtimePage
                                ? "text-pink-500"
                                : ""
                            }`}
                            data-oid="quhetnn"
                          >
                            {feature.title}
                            {isActive && isRealtimePage && (
                              <span
                                className="ml-2 inline-block h-1.5 w-1.5 rounded-full bg-pink-500"
                                data-oid="dx6du8-"
                              ></span>
                            )}
                          </div>
                          <p
                            className={`line-clamp-2 text-sm leading-snug 
                            ${
                              isRealtimePage
                                ? "text-muted-foreground group-hover:text-foreground/80"
                                : "text-muted-foreground group-hover:text-accent-foreground/80"
                            }`}
                            data-oid="5pqe8yv"
                          >
                            {feature.description}
                          </p>
                        </div>
                      </div>
                    </Link>
                  </NavigationMenuLink>
                );
              })}
            </motion.div>
          </NavigationMenuContent>
        </NavigationMenuItem>
      </NavigationMenuList>
    </NavigationMenu>
  );
}
